import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Grid,
  Heading,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import coachDashboardImage from "@root/public/images/screenshots/coach_dashboard.png";
import coachHomeImage from "@root/public/images/screenshots/coach_home.png";
import teamDashboardImage from "@root/public/images/screenshots/team_dashboard.png";
import Image from "next/image";
import { CaretIcon } from "./CaretIcon";
import { deepBlueSky } from "@/styles/theme";
import womanWithLaptop from "@root/public/images/photos/woman-with-laptop.png";
import Link from "next/link";
import { RoleToggler } from "./RoleToggler";
import { BulletPoint } from "./BulletPoint";

export const HowWeHelp = () => {
  const t = useTranslations();
  return (
    <Box
      bgColor="brand.900"
      color="brand.1000"
      w="full"
      h="full"
      pb={{
        base: 12,
        md: 20,
      }}
    >
      <Container maxW="container.xl">
        <Stack
          direction={["column", "row"]}
          w="full"
          h="full"
          py={{ base: 16, md: 20 }}
          px={{
            base: 4,
            md: 20,
          }}
          pb={{
            base: 4,
            md: 8,
          }}
          gap={{
            base: 8,
            md: 24,
          }}
        >
          <Box
            display={{
              base: "none",
              md: "block",
            }}
            flex={{
              base: 1,
              md: 3,
            }}
            h="full"
          >
            <VStack
              w="full"
              gap={10}
              style={{
                backgroundImage:
                  "url('/images/blue-circle.svg'), url('/images/blue-circle.svg')",
              }}
              bgRepeat={"no-repeat"}
              bgSize={"90%"}
              backgroundPosition={"0 top, 0 600px"}
              py={16}
              pl={10}
            >
              <Image
                src={coachHomeImage}
                alt="Dashboard"
                quality={80}
                style={{
                  borderRadius: "16px",
                  maxWidth: "500px",
                  boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
                }}
              />
              <CaretIcon />
              <Image
                src={coachDashboardImage}
                alt="Dashboard"
                quality={80}
                style={{
                  borderRadius: "16px",
                  maxWidth: "500px",
                  boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
                }}
              />
              <CaretIcon />
              <Image
                src={teamDashboardImage}
                alt="Dashboard"
                quality={80}
                style={{
                  borderRadius: "16px",
                  maxWidth: "500px",
                  boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
                }}
              />
            </VStack>
          </Box>
          <Box
            flex={{
              base: 1,
              md: 2,
            }}
            h="full"
          >
            <Heading
              fontFamily={"Gotham"}
              mb={{
                base: 8,
                md: 32,
              }}
            >
              {t("HowWeHelp.title")}
            </Heading>
            <Heading fontFamily={"Gotham"} mb={8}>
              {t("HowWeHelp.how")}
            </Heading>
            <Stack direction={"column"} gap={8} mb={24}>
              <BulletPoint>{t("HowWeHelp.howItem1")}</BulletPoint>
              <BulletPoint>{t("HowWeHelp.howItem2")}</BulletPoint>
              <BulletPoint>{t("HowWeHelp.howItem3")}</BulletPoint>
              <BulletPoint>{t("HowWeHelp.howItem4")}</BulletPoint>
              <BulletPoint>{t("HowWeHelp.howItem5")}</BulletPoint>
              <BulletPoint>{t("HowWeHelp.howItem6")}</BulletPoint>
            </Stack>
            <Heading fontFamily={"Gotham"} mb={8}>
              {t("HowWeHelp.coachingTeam")}
            </Heading>
            <Text mb={8}>{t("HowWeHelp.coachingTeamDescription")}</Text>
            <Stack direction={"column"} gap={8} mb={16}>
              <BulletPoint>{t("HowWeHelp.coachingTeam1")}</BulletPoint>
              <BulletPoint>{t("HowWeHelp.coachingTeam2")}</BulletPoint>
              <BulletPoint>{t("HowWeHelp.coachingTeam3")}</BulletPoint>
            </Stack>
          </Box>
        </Stack>
        <VStack
          w="full"
          px={{
            base: 4,
            md: 20,
          }}
          mb={16}
          gap={16}
        >
          <Divider borderColor="black.900" orientation="vertical" h={24} />
          <Flex
            w="full"
            direction={["column", "row"]}
            bgColor="black.900"
            color="white"
            rounded="lg"
            py={{ base: 4, md: 16 }}
            px={{ base: 4, md: 16 }}
            bgImage={`url('/images/world-bg.svg')`}
            bgRepeat={"no-repeat"}
            bgSize={"cover"}
            bgPosition={"center"}
          >
            <Box flex={3}>
              <Heading fontSize="2.5rem" mb={12} color={deepBlueSky}>
                {t("HowWeHelp.theresMore")}
              </Heading>
              <Heading
                fontSize={"3xl"}
                dangerouslySetInnerHTML={{
                  __html: t.raw("HowWeHelp.listYourProfile"),
                }}
              />
            </Box>
            <Box flex={2} display={{ base: "none", md: "block" }}></Box>
          </Flex>
          <Grid
            gridTemplateColumns={{
              base: "1fr",
              md: "1fr 1fr",
            }}
            gap={{
              base: 8,
              md: 20,
            }}
            w="full"
            alignItems={"center"}
            my={8}
          >
            <Container maxW="400px">
              <Image src={womanWithLaptop} alt="Woman with laptop" />
            </Container>
            <Box>
              <Heading
                textAlign={{
                  base: "center",
                  md: "left",
                }}
                fontWeight={400}
                dangerouslySetInnerHTML={{
                  __html: t.raw("HowWeHelp.timeToGrow"),
                }}
                mb={16}
              />
              <Link href={"/pricing"}>
                <Button
                  py={2}
                  fontSize={{ base: "22px", md: "28px" }}
                  fontFamily={`'Marlide', sans-serif`}
                  variant="secondary"
                  fontWeight={900}
                  px={6}
                  letterSpacing=".1rem"
                  w={{
                    base: "full",
                    md: "auto",
                  }}
                >
                  Let&apos;s Clarifayo
                </Button>
              </Link>
            </Box>
          </Grid>
        </VStack>
        <RoleToggler />
      </Container>
    </Box>
  );
};
